import CollectionUtils from "../CollectionHelper";

export function resolveFlowInstanceSelection(flowInstances, selection) {
  const list = Array.isArray(flowInstances) ? flowInstances : [];
  if (list.length === 0) {
    return { index: -1, instance: undefined };
  }
  if (typeof selection === "string") {
    const trimmed = selection.trim();
    if (trimmed.length === 0) {
      selection = undefined;
    } else {
      const matchIdx = list.findIndex(
        (instance) => instance?.id === trimmed || `${instance?.plainId}` === trimmed
      );
      if (matchIdx !== -1) {
        return { index: matchIdx, instance: list[matchIdx] };
      }
      const numericValue = Number(trimmed);
      selection = Number.isFinite(numericValue) ? numericValue : undefined;
    }
  }
  let numericIndex = typeof selection === "number" ? selection : Number(selection);
  if (!Number.isFinite(numericIndex)) {
    numericIndex = 0;
  }
  if (numericIndex < 0) {
    numericIndex = 0;
  }
  if (numericIndex >= list.length) {
    numericIndex = list.length - 1;
  }
  return { index: numericIndex, instance: list[numericIndex] };
}

export function mergeAxisSettings(baseAxis, overrideAxis) {
  if (!baseAxis && !overrideAxis) {
    return undefined;
  }
  if (!baseAxis) {
    return { ...(overrideAxis ?? {}) };
  }
  if (!overrideAxis) {
    return { ...baseAxis };
  }
  return {
    ...baseAxis,
    ...overrideAxis,
  };
}

export function mergeExploreConfig(baseExplore = {}, overrideExplore = {}) {
  const mergedFilters = [];
  const baseFilters = Array.isArray(baseExplore.filters) ? baseExplore.filters : [];
  const overrideFilters = Array.isArray(overrideExplore.filters) ? overrideExplore.filters : [];
  const limit = Math.max(baseFilters.length, overrideFilters.length);
  for (let i = 0; i < limit; i += 1) {
    if (baseFilters[i] || overrideFilters[i]) {
      mergedFilters[i] = {
        ...(baseFilters[i] ?? {}),
        ...(overrideFilters[i] ?? {}),
      };
    }
  }
  const mergedAxisBase = {
    ...(baseExplore.axis ?? {}),
    ...(overrideExplore.axis ?? {}),
  };
  return {
    ...baseExplore,
    ...overrideExplore,
    axis: {
      ...mergedAxisBase,
      column: mergeAxisSettings(baseExplore.axis?.column, overrideExplore.axis?.column),
      row: mergeAxisSettings(baseExplore.axis?.row, overrideExplore.axis?.row),
    },
    filters: mergedFilters,
  };
}

export function mergeReferenceParameters(configParent, primitive) {
  const parentParams = configParent?.referenceParameters ?? {};
  const localParams = primitive?.referenceParameters ?? {};
  const merged = {
    ...parentParams,
    ...localParams,
  };
  if (parentParams.explore || localParams.explore) {
    merged.explore = mergeExploreConfig(parentParams.explore ?? {}, localParams.explore ?? {});
  }
  return merged;
}

function resolveViewConfig(viewConfigs = [], requestedView) {
  const match = CollectionUtils.matchViewConfig(viewConfigs, requestedView);
  return match.config;
}

export function buildViewRenderInput({
  primitive,
  width,
  height,
  lockDimensions = false,
} = {}) {
  if (!primitive) {
    return null;
  }
  const configParent = primitive.configParent ?? null;
  const mergedRenderConfig = {
    ...(configParent?.renderConfig ?? {}),
    ...(primitive.renderConfig ?? {}),
  };
  const mergedReferenceParameters = mergeReferenceParameters(configParent, primitive);
  const resolveConfig = (configValue) =>
    typeof configValue === "function" ? configValue() : configValue;
  const primitiveConfig = resolveConfig(primitive.getConfig ?? null);
  const parentConfig = resolveConfig(configParent?.getConfig ?? null);
  const exploreConfig = primitiveConfig?.explore ?? mergedReferenceParameters.explore ?? {};
  const items = primitive.itemsForProcessing ?? [];
  const metadataSource = items[0]?.metadata ?? primitive.metadata ?? configParent?.metadata;
  const viewConfigs =
    CollectionUtils.resolveViewConfigsForPrimitive(primitive, metadataSource) ?? [];
  let viewConfig = resolveViewConfig(viewConfigs, exploreConfig?.view);
  if (!viewConfig) {
    viewConfig = { renderType: "grid", dataConfig: {} };
  }

  const fallbackConfig =
    primitiveConfig ??
    parentConfig ?? {
      ...mergedReferenceParameters,
      explore: exploreConfig,
    };
  const dataSourcePrimitive =
    primitiveConfig
      ? primitive
      : parentConfig
        ? {
            ...primitive,
            referenceParameters: mergedReferenceParameters,
            metadata: metadataSource,
            renderConfig: mergedRenderConfig,
            primitives: primitive.primitives ?? configParent?.primitives ?? {},
            inputs: primitive.inputs ?? configParent?.inputs ?? {},
            getConfig: parentConfig,
          }
        : {
            ...primitive,
            referenceParameters: mergedReferenceParameters,
            metadata: metadataSource,
            renderConfig: mergedRenderConfig,
            primitives: primitive.primitives ?? configParent?.primitives ?? {},
            inputs: primitive.inputs ?? configParent?.inputs ?? {},
            getConfig: fallbackConfig,
          };

  const dataTable = CollectionUtils.createDataTableForPrimitive(
    dataSourcePrimitive,
    viewConfig?.dataConfig ?? {},
    items
  );
  if (!dataTable) {
    return null;
  }

  const rawViewMode = viewConfig?.renderType ?? viewConfig?.matrixType ?? "grid";
  const normalizedViewMode = typeof rawViewMode === "string" ? rawViewMode.toLowerCase() : "grid";
  const isGridLike =
    !normalizedViewMode ||
    normalizedViewMode === "grid" ||
    normalizedViewMode.includes("grid") ||
    normalizedViewMode.includes("heatmap") ||
    normalizedViewMode.includes("matrix");
  const shouldForceDimensions = lockDimensions && !isGridLike;

  const renderOptionsOverrides = {
    ...mergedRenderConfig,
  };
  if (shouldForceDimensions && width && height) {
    renderOptionsOverrides.width = width;
    renderOptionsOverrides.height = height;
    renderOptionsOverrides.tableWidth = width;
    renderOptionsOverrides.tableHeight = height;
  }

  return {
    id: primitive.id ?? primitive.plainId ?? "view_render",
    primitive,
    data: dataTable,
    renderOptions: renderOptionsOverrides,
    viewConfig,
  };
}
